import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { MICROTASK_MS } from '@/shared/lib/constants';

interface DatePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectDate: (date: Date | null) => void;
  selectedDate: Date | null;
  selectedTime?: string;
  onSelectTime?: (time: string) => void;
  timeOptions?: string[];
  withTime?: boolean;
  availabilityText?: string;
  multiSelect?: boolean;
  selectedDates?: Date[];
  onToggleDate?: (date: Date) => void;
  selectableDateKeys?: string[];
  asInline?: boolean;
  blackoutDates?: string[];
  soldOutDates?: string[];
  soldOutTimes?: string[];
  weeklySelectionMode?: boolean;
  selectedWeekDays?: number[];
  onToggleWeekDay?: (dayValue: number) => void;
}

const toDateKey = (date: Date) => {
  const year = String(date.getUTCFullYear());
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export function DatePickerModal({
  isOpen,
  onClose,
  onSelectDate,
  selectedDate,
  selectedTime,
  onSelectTime,
  timeOptions = [],
  withTime = false,
  availabilityText,
  multiSelect = false,
  selectedDates = [],
  onToggleDate,
  selectableDateKeys = [],
  asInline = false,
  blackoutDates = [],
  soldOutDates = [],
  soldOutTimes = [],
  weeklySelectionMode = false,
  selectedWeekDays = [],
  onToggleWeekDay,
}: DatePickerModalProps) {
  const { t, i18n } = useTranslation();
  const [month, setMonth] = useState(() => {
    const initial = selectedDate ?? new Date();
    return new Date(initial.getFullYear(), initial.getMonth(), 1);
  });
  const selectableDateKeySet = new Set(selectableDateKeys);
  const blackoutDatesSet = new Set(blackoutDates);
  const soldOutDatesSet = new Set(soldOutDates);
  const soldOutTimesSet = new Set(soldOutTimes);

  useEffect(() => {
    if (multiSelect || asInline) return;
    if (isOpen && !selectedDate) {
      const targetDate = new Date();
      targetDate.setHours(0, 0, 0, 0);
      // Use a timeout or a microtask to avoid synchronous state update in effect
      const timeout = setTimeout(() => {
        onSelectDate(targetDate);
      }, MICROTASK_MS);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isOpen, selectedDate, onSelectDate, multiSelect, asInline]);

  useEffect(() => {
    if (!withTime) return;
    if (!timeOptions.length) return;
    if (!selectedTime || !timeOptions.includes(selectedTime)) {
      onSelectTime?.(timeOptions[0]);
    }
  }, [withTime, onSelectTime, selectedTime, timeOptions]);

  const getDaysInMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const days = Array.from({ length: getDaysInMonth(month) }, (_, i) => i + 1);
  const firstDay = getFirstDayOfMonth(month);
  const emptyDaysCount = (firstDay + 6) % 7;
  const emptyDays = Array.from({ length: emptyDaysCount }, (_, i) => i);

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() && month.getMonth() === today.getMonth() && month.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day: number) => {
    if (weeklySelectionMode && selectedWeekDays) {
      const dayDate = new Date(month.getFullYear(), month.getMonth(), day);
      const dayValue = dayDate.getDay() === 0 ? 7 : dayDate.getDay();
      return selectedWeekDays.includes(dayValue);
    }
    if (multiSelect) {
      return selectedDates.some(
        (date) =>
          date.getDate() === day && month.getMonth() === date.getMonth() && month.getFullYear() === date.getFullYear()
      );
    }
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      month.getMonth() === selectedDate.getMonth() &&
      month.getFullYear() === selectedDate.getFullYear()
    );
  };

  const isPastDay = (day: number) => {
    const dayDate = new Date(month.getFullYear(), month.getMonth(), day);
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    return dayDate < todayStart;
  };

  const toUtcDate = (day: number) => new Date(Date.UTC(month.getFullYear(), month.getMonth(), day));

  const isBlackoutDay = (day: number) => {
    const dayDate = toUtcDate(day);
    return blackoutDatesSet.has(toDateKey(dayDate));
  };
  const isSoldOutDay = (day: number) => {
    const dayDate = toUtcDate(day);
    return soldOutDatesSet.has(toDateKey(dayDate));
  };
  const isSelectableDay = (day: number) => {
    const dayDate = toUtcDate(day);
    if (isPastDay(day)) return false;
    if (isBlackoutDay(day)) return false;
    if (isSoldOutDay(day)) return false;
    if (weeklySelectionMode) return true;
    if (selectableDateKeySet.size === 0) return true;
    return selectableDateKeySet.has(toDateKey(dayDate));
  };

  const hasAvailabilityDay = (day: number) => {
    if (weeklySelectionMode) return false;
    if (isBlackoutDay(day) || isSoldOutDay(day)) return false;
    if (selectableDateKeySet.size === 0) return false;
    const dayDate = toUtcDate(day);
    return selectableDateKeySet.has(toDateKey(dayDate));
  };

  const handleSelectDay = (day: number) => {
    if (isBlackoutDay(day) || isSoldOutDay(day)) return;
    const newDate = toUtcDate(day);

    if (weeklySelectionMode) {
      const dayValue = newDate.getDay() === 0 ? 7 : newDate.getDay();
      onToggleWeekDay?.(dayValue);
      return;
    }

    if (selectableDateKeySet.size > 0 && !selectableDateKeySet.has(toDateKey(newDate))) return;

    if (multiSelect) {
      onToggleDate?.(newDate);
      return;
    }

    onSelectDate(newDate);
    if (!withTime) onClose();
  };

  const getDayButtonClass = (day: number) => {
    const isSelectable = isSelectableDay(day);
    const baseClass = 'rounded-xl py-2.5 text-sm font-bold transition-all ';
    if (!isSelectable) return baseClass + 'cursor-not-allowed bg-gray-50 text-gray-300';
    if (isSelected(day)) {
      if (weeklySelectionMode) {
        return baseClass + 'bg-lime-900/10 text-lime-900 border border-lime-200/50 shadow-sm';
      }
      return baseClass + 'bg-lime-900 text-white shadow-md';
    }
    if (isToday(day)) return baseClass + 'bg-lime-100 text-lime-900';
    return baseClass + 'bg-gray-100 text-gray-900 hover:bg-gray-200';
  };

  const today = new Date();
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const maxMonthStart = new Date(today.getFullYear(), today.getMonth() + 4, 1);

  const isPrevDisabled = month <= currentMonthStart;
  const isNextDisabled = month >= maxMonthStart;

  const handlePrevMonth = useCallback(() => {
    setMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1));
  }, []);

  const handleNextMonth = useCallback(() => {
    setMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1));
  }, []);

  const content = (
    <div className="flex flex-col z-40">
      {/* Month Navigation */}
      <div className="mb-6 flex items-center justify-between">
        <button
          type="button"
          disabled={isPrevDisabled}
          onClick={handlePrevMonth}
          className={`flex h-10 w-10 items-center justify-center rounded-full font-bold transition-colors ${
            isPrevDisabled
              ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }`}
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="heading-3 capitalize">
          {month.toLocaleDateString(i18n.language || 'en-US', { month: 'long' })} {month.getFullYear()}
        </span>
        <button
          type="button"
          disabled={isNextDisabled}
          onClick={handleNextMonth}
          className={`flex h-10 w-10 items-center justify-center rounded-full font-bold transition-colors ${
            isNextDisabled
              ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }`}
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Calendar Grid */}
      <div className="grid grid-cols-7 gap-2 text-center items-center">
        {Array.from({ length: 7 }).map((_, i) => {
          const d = new Date(2024, 0, i + 1); // 2024-01-01 is a Monday
          const dayValue = i + 1;
          const isWeekDaySelected = selectedWeekDays?.includes(dayValue);

          if (weeklySelectionMode) {
            return (
              <button
                key={`dp-wd-${i}`}
                type="button"
                onClick={() => onToggleWeekDay?.(dayValue)}
                className={`w-full py-2.5 meta-upper transition-all rounded-xl border ${
                  isWeekDaySelected
                    ? 'bg-lime-900 border-lime-900 text-lime-400 shadow-md shadow-lime-900/10 hover:bg-lime-950'
                    : 'bg-gray-50 border-gray-100 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                }`}
              >
                {d.toLocaleDateString(i18n.language || 'en-US', { weekday: 'short' })}
              </button>
            );
          }

          return (
            <div key={`dp-d-${i}`} className="eyebrow text-gray-400 pb-2">
              {d.toLocaleDateString(i18n.language || 'en-US', { weekday: 'short' })}
            </div>
          );
        })}
        {emptyDays.map((_, i) => (
          <div key={`empty-${i.toString()}`} />
        ))}
        {days.map((day) => (
          <button
            type="button"
            key={day}
            onClick={() => handleSelectDay(day)}
            disabled={!isSelectableDay(day)}
            className={`${getDayButtonClass(day)} relative`}
          >
            {day}
            {hasAvailabilityDay(day) && !isSelected(day) ? (
              <span className="pointer-events-none absolute bottom-1.5 left-1/2 h-1 w-1 -translate-x-1/2 rounded-full bg-lime-400" />
            ) : null}
          </button>
        ))}
      </div>

      {/* Legend / Availability */}
      {selectableDateKeySet.size > 0 && (
        <div className="mt-4 flex items-center gap-2 eyebrow text-gray-500">
          <span className="h-2 w-2 rounded-full bg-lime-400" />
          {t('common.availableDays', 'Available Days')}
        </div>
      )}

      {availabilityText && (
        <div className="mt-4 rounded-2xl border border-lime-100 bg-lime-50 px-4 py-3 nav-label !text-lime-700">
          {availabilityText}
        </div>
      )}

      {/* Exact Time Slots */}
      {withTime && !multiSelect && (
        <div className="mt-6 border-t border-gray-50 pt-6">
          <p className="mb-3 eyebrow text-gray-400">{t('common.selectTime', 'Select time')}</p>
          <div className="flex flex-wrap gap-2">
            {timeOptions.length > 0 ? (
              timeOptions.map((slot) => {
                const isSoldOut = soldOutTimesSet.has(slot);
                return (
                  <button
                    key={slot}
                    type="button"
                    disabled={isSoldOut}
                    onClick={() => !isSoldOut && onSelectTime?.(slot)}
                    className={`rounded-xl px-4 py-2.5 text-sm font-bold transition-all ${
                      isSoldOut
                        ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                        : selectedTime === slot
                          ? 'bg-lime-900 text-white shadow-md'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {slot}
                  </button>
                );
              })
            ) : (
              <p className="text-xs font-bold text-gray-400">{t('common.noTimeSlots', 'No time slots available')}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );

  if (asInline) return content;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={multiSelect ? t('common.selectDates', 'Select dates') : t('common.selectDate', 'Select date')}
      size="md"
      zIndex={510}
    >
      {content}
    </Modal>
  );
}
